import { useState, useEffect } from 'react';
import { Card, CardHeader, CardBody } from '@/shared/components/ui/Card';
import { Skeleton } from '@/shared/components/ui/Skeleton';
import { ShieldAlert, Search } from 'lucide-react';
import { api } from '@/shared/api/axios';

export function AuditLogViewer() {
  const [logs, setLogs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    api.get('/api/accounting/audit-logs')
      .then((res) => {
        const data = res.data;
        if (data.ok || data.success) {
          const list = Array.isArray(data.data) ? data.data : Array.isArray(data.items) ? data.items : [];
          setLogs(list);
        } else {
          setLogs([]);
        }
      })
      .catch((err) => {
        setError(err.response?.data?.error?.message || err.message);
        setLogs([]);
      })
      .finally(() => setLoading(false));
  }, []);

  const safeFormatDate = (dateVal: any) => {
    try {
      const d = dateVal ? new Date(dateVal) : new Date();
      if (isNaN(d.getTime())) return 'Just now';
      return d.toLocaleString('en-IN', {
        dateStyle: 'medium',
        timeStyle: 'short',
      });
    } catch (e) {
      return 'Just now';
    }
  };

  const filteredLogs = logs.filter((l) => {
    if (!search) return true;
    const term = search.toLowerCase();
    return (
      (l.action && l.action.toLowerCase().includes(term)) ||
      (l.entityType && l.entityType.toLowerCase().includes(term)) ||
      (l.actorName && l.actorName.toLowerCase().includes(term)) ||
      (l.actorId && l.actorId.toLowerCase().includes(term)) ||
      (l.entityId && String(l.entityId).toLowerCase().includes(term))
    );
  });

  if (loading) {
    return <Skeleton className="h-[400px] w-full" />;
  }

  return (
    <Card className="shadow-xs h-full flex flex-col bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl overflow-hidden">
      <CardHeader
        className="flex-none border-b border-slate-200 dark:border-slate-800 p-4"
        title={
          <div className="flex items-center gap-2 font-bold text-base">
            <ShieldAlert className="w-5 h-5 text-indigo-500" />
            Immutable Audit Trail Logs
          </div>
        }
        subtitle="Tamper-evident activity record of all tenant actions, postings, and settings changes."
        action={
          <div className="relative w-64">
            <Search className="w-4 h-4 absolute left-3 top-2.5 text-slate-400" />
            <input
              type="text"
              placeholder="Filter audit events..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 pr-3 py-1.5 border border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 rounded-lg text-xs w-full"
            />
          </div>
        }
      />

      <CardBody className="flex-1 overflow-auto p-0">
        {error ? (
          <div className="p-8 text-center text-rose-500 text-xs font-semibold">{error}</div>
        ) : (
          <div className="divide-y divide-slate-100 dark:divide-slate-800">
            <div className="grid grid-cols-12 gap-4 bg-slate-50 dark:bg-slate-800/60 py-3 px-4 border-b border-slate-200 dark:border-slate-800 text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wider">
              <div className="col-span-3">Timestamp</div>
              <div className="col-span-2">Actor / User</div>
              <div className="col-span-2">Action Event</div>
              <div className="col-span-2">Entity Type</div>
              <div className="col-span-3 text-right">Entity Reference / IP</div>
            </div>

            {filteredLogs.map((log, idx) => (
              <div
                key={log._id || `log-${idx}`}
                className="grid grid-cols-12 gap-4 py-3.5 px-4 text-xs hover:bg-slate-50/80 dark:hover:bg-slate-800/40 transition-colors items-center"
              >
                <div className="col-span-3 font-mono text-slate-500">
                  {safeFormatDate(log.createdAt || log.timestamp)}
                </div>
                <div className="col-span-2 font-bold text-slate-900 dark:text-slate-100">
                  {log.actorName || log.actorId || log.userId?.displayName || 'System'}
                </div>
                <div className="col-span-2">
                  <span className="px-2 py-0.5 rounded text-[10px] uppercase font-bold bg-indigo-100 text-indigo-800 dark:bg-indigo-950 dark:text-indigo-300">
                    {log.action}
                  </span>
                </div>
                <div className="col-span-2 font-medium text-slate-600 dark:text-slate-400">
                  {log.entityType}
                </div>
                <div className="col-span-3 text-right font-mono text-[11px] text-slate-500 truncate" title={log.entityId}>
                  ID: {log.entityId || 'N/A'} {log.ip ? `(${log.ip})` : ''}
                </div>
              </div>
            ))}

            {filteredLogs.length === 0 && (
              <div className="p-12 text-center text-xs text-slate-400">
                No audit log records found matching search criteria.
              </div>
            )}
          </div>
        )}
      </CardBody>
    </Card>
  );
}

export default AuditLogViewer;
